import type { NonConformityRef } from '@obraplan/core/application/contracts/planning';
import type { Session } from '@obraplan/core/application/session';
import type { Container } from '@/app/container';
import type { PendingNonConformityPhotos } from './quality-verification-draft';

/** O que subiu das fotos escolhidas no modal, e o que não subiu, com o motivo. */
export interface FotosDasNcs {
  readonly enviadas: number;
  readonly falhas: readonly string[];
}

/**
 * A NC de cada foto escolhida no modal: a do mesmo item da mesma ficha, na mesma frente e serviço.
 * A fila do aparelho (`conference-operations.ts`) casa as fotos por aqui também.
 */
export function ncDaFoto(
  pendente: PendingNonConformityPhotos,
  refs: readonly NonConformityRef[],
): NonConformityRef | undefined {
  return refs.find(
    (candidata) =>
      candidata.activityId === pendente.activityId &&
      candidata.serviceId === pendente.serviceId &&
      candidata.checklistId === pendente.checklistId &&
      candidata.itemId === pendente.itemId,
  );
}

/** O motivo da foto que não tem dono: a NC do item dela não chegou a ser gravada. */
export function semNcGravada(file: File): string {
  return `${file.name}: a não conformidade do item não foi gravada`;
}

/**
 * Sobe as fotos que foram escolhidas no modal antes de a NC existir (Fase 37, RN-479).
 *
 * A gravação da medição devolve a NC de cada item (`NonConformityRef`), e é por ela que cada
 * foto acha o seu dono. Uma foto por vez, como a da conferência: a cota é contada a cada uma.
 * A foto que falha não desfaz a NC — ela já está gravada —, e o motivo volta para a tela dizer
 * qual não subiu.
 */
export async function subirFotosDasNcs(
  container: Pick<Container, 'attachNonConformityPhoto'>,
  session: Session,
  pendentes: readonly PendingNonConformityPhotos[],
  refs: readonly NonConformityRef[],
): Promise<FotosDasNcs> {
  let enviadas = 0;
  const falhas: string[] = [];
  for (const pendente of pendentes) {
    const ref = ncDaFoto(pendente, refs);
    if (ref === undefined) {
      falhas.push(...pendente.files.map(semNcGravada));
      continue;
    }
    for (const file of pendente.files) {
      const anexada = await container.attachNonConformityPhoto.execute(
        {
          nonConformityId: ref.nonConformityId,
          fileName: file.name,
          declaredMimeType: file.type,
          bytes: new Uint8Array(await file.arrayBuffer()),
        },
        session,
      );
      if (anexada.ok) enviadas += 1;
      else falhas.push(`${file.name}: ${anexada.error.message}`);
    }
  }
  return { enviadas, falhas };
}

/** As partes da mensagem de sucesso que falam das fotos — vazia quando não havia foto. */
export function mensagemDasFotos(fotos: FotosDasNcs): readonly string[] {
  return [
    ...(fotos.enviadas > 0
      ? [
          fotos.enviadas === 1
            ? '1 foto da não conformidade anexada'
            : `${String(fotos.enviadas)} fotos das não conformidades anexadas`,
        ]
      : []),
    ...(fotos.falhas.length > 0
      ? [
          `${fotos.falhas.length === 1 ? '1 foto não subiu' : `${String(fotos.falhas.length)} fotos não subiram`} (${fotos.falhas.join('; ')}) — anexe de novo na não conformidade`,
        ]
      : []),
  ];
}
